import React, { useEffect, useState } from "react";
import { api, assetUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Link } from "react-router-dom";
import {
  Sparkles,
  ExternalLink,
  QrCode,
  MoreVertical,
  Pause,
  Play,
  Trash2,
  Copy,
  BarChart3,
  Trophy,
  RefreshCw,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { TrialCountdownPill, ReactivateButton } from "@/components/vendor/Billing";
import BoostCampaignDialog from "@/components/vendor/BoostCampaignDialog";
import RenewListingDialog from "@/components/vendor/RenewListingDialog";

const STATUS_BADGE = {
  active: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/20",
  paused: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/20",
  expired: "bg-zinc-500/15 text-zinc-600 dark:text-zinc-300 border-zinc-500/20",
  pending_payment: "bg-amber-500/15 text-amber-700 border-amber-500/20",
  trial_ended: "bg-red-500/15 text-red-700 border-red-500/20",
};

const STATUS_LABEL = {
  active: "Active",
  paused: "Paused",
  expired: "Expired",
  pending_payment: "Pending payment",
  trial_ended: "Trial ended",
};

export default function CampaignsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boostFor, setBoostFor] = useState(null);
  const [renewFor, setRenewFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.listCampaigns();
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    load();
  }, []);

  const toggleStatus = async (c) => {
    const next = c.status === "active" ? "paused" : "active";
    await api.updateCampaign(c.id, { status: next });
    toast.success(`Campaign ${next}`);
    load();
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    await api.deleteCampaign(c.id);
    toast.success("Campaign deleted");
    load();
  };

  const copyLink = (c) => {
    navigator.clipboard.writeText(c.campaign_url || `${window.location.origin}/play/${c.id}`);
    toast.success("Link copied");
  };

  const regenQr = async (c) => {
    const r = await api.generateQr(c.id);
    toast.success("QR generated");
    setItems((arr) => arr.map((x) => (x.id === c.id ? { ...x, qr_code_url: r.data.qr_code_url } : x)));
  };

  const renewListing = (c) => setRenewFor(c);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold">Campaigns</h1>
          <p className="text-muted-foreground text-sm">Create and manage your spin wheel campaigns.</p>
        </div>
        <Link to="/vendor/campaigns/new">
          <Button data-testid="campaigns-new-button">
            <Sparkles className="mr-2 h-4 w-4" /> New campaign
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="grid gap-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <Card className="p-10 text-center" data-testid="campaigns-empty-state">
          <Sparkles className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="font-heading text-xl mt-3">No campaigns yet</h2>
          <p className="text-muted-foreground mt-1">Create your first spin wheel to start collecting leads.</p>
          <Link to="/vendor/campaigns/new">
            <Button className="mt-5" data-testid="campaigns-empty-cta">Create campaign</Button>
          </Link>
        </Card>
      ) : (
        <div className="grid gap-4">
          {items.map((c) => (
            <Card key={c.id} className="p-4 md:p-5 shadow-[var(--shadow-card)] hover:shadow-[0_18px_50px_rgba(16,24,40,0.12)] transition-shadow" data-testid="campaign-card">
              {/* Row wraps to a stacked layout under md so QR + text stay on
                  the first row and the action cluster drops to a full-
                  width row below it. Prevents the button strip from
                  overflowing the card at 360-430px. */}
              <div className="flex flex-wrap md:flex-nowrap items-start md:items-center gap-3 md:gap-4">
                <div className="h-14 w-14 md:h-16 md:w-16 shrink-0 rounded-xl bg-muted grid place-items-center overflow-hidden" data-testid="campaign-qr-thumbnail">
                  {c.qr_code_url ? (
                    <img src={assetUrl(c.qr_code_url)} alt="QR" className="w-full h-full object-cover" />
                  ) : (
                    <QrCode className="h-6 w-6 md:h-7 md:w-7 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-heading text-base md:text-lg font-semibold truncate max-w-full" data-testid="campaign-name">
                      {c.name}
                    </h3>
                    <Badge
                      variant="outline"
                      className={STATUS_BADGE[c.status] || ""}
                      data-testid="campaign-status-badge"
                    >
                      {STATUS_LABEL[c.status] || c.status}
                    </Badge>
                    <TrialCountdownPill campaign={c} />
                  </div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">
                    {c.theme} • {c.spin_limit?.replace("_", " ")} • {c.slices?.length || 0} slices
                  </p>
                </div>
                {/* Action cluster — full-width on mobile so all buttons stay
                    inside the viewport. wraps naturally on tight screens. */}
                <div className="w-full md:w-auto flex flex-wrap items-center gap-2 pt-2 md:pt-0 border-t md:border-t-0 border-border/60 mt-1 md:mt-0">
                  {(c.status === "pending_payment" || c.status === "trial_ended" || c.status === "paused") && (
                    <ReactivateButton campaign={c} onResumed={load} />
                  )}
                  <Link to={`/vendor/campaigns/${c.id}/analytics`} className="flex-1 md:flex-none">
                    <Button variant="outline" size="sm" className="w-full md:w-auto" data-testid="campaign-analytics-button">
                      <BarChart3 className="h-4 w-4 mr-1" /> Analytics
                    </Button>
                  </Link>
                  <Link to={`/play/${c.id}`} target="_blank" className="flex-1 md:flex-none">
                    <Button variant="outline" size="sm" className="w-full md:w-auto" data-testid="campaign-open-button">
                      <ExternalLink className="h-4 w-4 mr-1" /> Open
                    </Button>
                  </Link>
                  <Link to={`/vendor/campaigns/${c.id}`} className="flex-1 md:flex-none">
                    <Button variant="secondary" size="sm" className="w-full md:w-auto" data-testid="campaign-edit-button">Edit</Button>
                  </Link>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" aria-label="more" data-testid="campaign-actions-menu">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => copyLink(c)}>
                        <Copy className="h-4 w-4 mr-2" /> Copy link
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => regenQr(c)}>
                        <QrCode className="h-4 w-4 mr-2" /> Regenerate QR
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setBoostFor(c)} data-testid="campaign-boost-menu-item">
                        <Trophy className="h-4 w-4 mr-2" /> Boost campaign
                      </DropdownMenuItem>
                      {c.game_type === "static_offer" && (
                        <DropdownMenuItem onClick={() => renewListing(c)} data-testid="campaign-renew-listing-menu-item">
                          <RefreshCw className="h-4 w-4 mr-2" /> Renew listing
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem onClick={() => toggleStatus(c)}>
                        {c.status === "active" ? (
                          <><Pause className="h-4 w-4 mr-2" /> Pause</>
                        ) : (
                          <><Play className="h-4 w-4 mr-2" /> Resume</>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => remove(c)} className="text-destructive">
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <BoostCampaignDialog
        open={!!boostFor}
        onOpenChange={(v) => !v && setBoostFor(null)}
        campaign={boostFor}
        onSuccess={load}
      />
      <RenewListingDialog
        open={!!renewFor}
        onOpenChange={(v) => !v && setRenewFor(null)}
        campaign={renewFor}
        onSuccess={load}
      />
    </div>
  );
}
